import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { ProjectRelativeLink } from '@/components/navigation';
import { getProject, addProjectMemberInternal } from '@/services/projects';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { ValidationForm, ValidationSummary } from '@/components/forms/validation';
import ProjectPermissionsEditor from '@/seller/components/project/UserManagement/ProjectPermissionsEditor';
import { Button } from '@/components/Buttons';
import { useAsyncState } from '@/utils/hooks';
import { ProjectMemberPermissions, ProfileMember } from '@/types';
import { connect } from 'react-redux';
import { IState } from '@/state/IState';
import { ProfileUsersAutocomplete } from '@/components/Autocomplete';
import ProjectInviteWrapper from './ProjectInviteWrapper';

const mapStateToProps = (state: IState) => ({
    projectId: state.global.activeProject!.id,
    profileId: state.global.activeProfile!.id,
});

type OwnProps = ReturnType<typeof mapStateToProps> & {};

const ProjectInviteInternal: React.FC<OwnProps> = ({ projectId, profileId }) => {
    const [selectedMember, setSelectedMember] = useState<ProfileMember | null>(null);
    const [permissions, setPermissions] = useState<ProjectMemberPermissions>({
        categoryPermissions: [],
        projectPermissions: [],
        allowedProjectFeeSetIds: [],
        isAdministrator: false,
    });
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [project] = useAsyncState(() => getProject(projectId), [projectId], null);

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (!selectedMember) {
            return;
        }

        setSaving(true);

        try {
            await addProjectMemberInternal(projectId, selectedMember.email, permissions);
            setSuccess(true);
        } finally {
            setSaving(false);
        }
    };

    if (!project) {
        return <LoadingIndicator />;
    }

    if (success) {
        return (
            <ProjectInviteWrapper projectName={project.name}>
                <h3>
                    <Trans>{selectedMember!.displayName} has been added to the project</Trans>
                </h3>
                <ProjectRelativeLink className="link-unstyled" to={`/users`}>
                    <Button color="primary">
                        <Trans>Back to project permissions</Trans>
                    </Button>
                </ProjectRelativeLink>
            </ProjectInviteWrapper>
        );
    }

    return (
        <ProjectInviteWrapper projectName={project.name}>
            <ValidationForm onSubmit={handleFormSubmit} className="form columns-1">
                <div className="form-group">
                    <ProfileUsersAutocomplete
                        className="form-control"
                        profileId={profileId}
                        onChange={(member) => setSelectedMember(member)}
                    />
                </div>

                <div className="form-group">
                    <ProjectPermissionsEditor
                        projectId={projectId}
                        value={permissions}
                        onChange={(value) => setPermissions(value)}
                    />
                </div>

                <div className="label-offset">
                    <ValidationSummary />
                </div>

                <div className="buttons right">
                    <Button type="submit" color="primary" loading={saving}>
                        <Trans>Add user</Trans>
                    </Button>
                </div>
            </ValidationForm>
        </ProjectInviteWrapper>
    );
};

export default connect(mapStateToProps)(ProjectInviteInternal);
